import { useEffect, useState, useRef, useMemo } from "react";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import {
  Typography,
  Box,
  Button,
  Paper,
  Icon,
  Fab,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import ConfirmationDialog from "components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import { useAppTheme } from "context/themeContext";
import { updateTrainingRecord, deleteTrainingRecordById, addTrainingRecord } from "services/api";
import { Divider } from "@mui/material";
import AddLAndDevDialog from "components/AddLAndDevDialog";
import MDBox from "components/MDBox";
import {
  GapAnalysisChart,
  ProgressChart,
} from "layouts/user/Competencies/learningAndDevelopment/components/Charts";
import TrainingCard from "layouts/user/Competencies/learningAndDevelopment/components/TrainingsCard";

const LearningAndDevelopment = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [editingRows, setEditingRows] = useState({});
  const snackbarRef = useRef();
  const [confOpen, setConfOpen] = useState(false);
  const navigate = useNavigate();
  const { primaryColor } = useAppTheme();

  const {
    selectedEmployeeId,
    selectedEmployeeTraining: initialTraining,
    mapTraining,
    trainingCompMap,
    empTrainingOptions,
    jobLvls,
    empCompGaps,
    selectedLvlIds: lvlIds,
  } = location.state || {}; // fallback in case state is undefined

  console.log("Initial Training::", initialTraining);
  const [selectedEmpTraining, setSelectedEmpTraining] = useState(initialTraining || []);
  const [selectedLevelIds, setSelectedLevelIds] = useState(lvlIds || []);
  const [savingDraftEmpId, setSavingDraftEmpId] = useState(null);

  const [savingRowIndex, setSavingRowIndex] = useState(null);
  const [originalRows, setOriginalRows] = useState({});
  const [confirmedConflictEmpIds, setConfirmedConflictEmpIds] = useState(new Set());
  const [openTrainingDialog, setOpenTrainingDialog] = useState(false);
  const competencies = Object.values(empCompGaps || {});
  const employeeName = competencies[0].empName ?? `Employee ${empId}`;
  const labels = competencies.map((c) => c.competencyName); //creates a new array that contains only the names of the comps
  console.log("Labels::", labels);
  const gapData = competencies.map((c) => c.gap); //extracts the gap values from each competency object
  console.log("Gap Data::", gapData);
  const empId = selectedEmployeeId; // single employee
  const [rowToDelete, setRowToDelete] = useState({ id: null, index: null });

  if (!selectedEmpTraining || !jobLvls || !empCompGaps) {
    return null; // render nothing until data is ready
  }

  const formatToLocalDateTime = (dateStr) => {
    return new Date(dateStr).toISOString().split(".")[0]; //
  };

  const { totalTrainings, finishedTrainings } = useMemo(() => {
    const total = selectedEmpTraining?.length || 0;
    const finished =
      selectedEmpTraining?.filter((training) => training[3] && new Date(training[3]) <= new Date())
        .length || 0;

    return {
      totalTrainings: total,
      finishedTrainings: finished,
    };
  }, [selectedEmpTraining]);

  const handleAchievedLevelChange = async (e, index) => {
    const updated = [...selectedEmpTraining]; // Create a shallow copy of the current selected employee training rows

    const recordId = updated[index][8]; //Get the training record ID from the updated row
    const levelId = Number(e.target.value); // Get the newly selected level ID from the dropdown menu.
    const competencyId = updated[index][5]; //Get the competency ID for the current training

    const selectedLevel = jobLvls.find(
      (lvl) => lvl.competencyId === competencyId && lvl.id === levelId
    );

    if (!selectedLevel) return;

    // ✅ Update UI immediately
    updated[index][10] = selectedLevel.name;
    updated[index][11] = selectedLevel.value;
    updated[index][9] = selectedLevel.id;

    setSelectedEmpTraining(updated);

    const updatePayload = {
      achievedLevel: selectedLevel.id,
    };

    try {
      const res = await updateTrainingRecord(recordId, updatePayload);

      if (res !== "error") {
        const newOriginalRows = { ...originalRows };
        newOriginalRows[index] = [...updated[index]];
        setOriginalRows(newOriginalRows);

        snackbarRef.current?.showSnackbar(t("user.trainings.trainingUpdated"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("user.trainings.trainingUpdatedFailed"), "error");
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("user.trainings.trainingUpdatedFailed"), "error");
    }
  };

  //For showing the expected level when opening edit mode
  useEffect(() => {
    const updated = {};
    selectedEmpTraining.forEach((row, index) => {
      if (editingRows[index]) {
        updated[index] = row[4] || ""; // Expected Level ID
      }
    });
    setSelectedLevelIds((prev) => ({ ...prev, ...updated }));
  }, [editingRows, selectedEmpTraining]);

  //UI Changes For Training Updates
  const handleTrainingFieldChange = (e, index, empId, key) => {
    const updated = [...selectedEmpTraining]; //copy of the selectedEmpTraining array
    const original = originalRows[index];

    if (key === 0) {
      // Training name change
      const selectedTrainingId = e.target.value;
      const training = Object.values(mapTraining)
        .flat()
        .find((t) => t.id === selectedTrainingId);

      if (training) {
        updated[index][0] = training.id; // trainingId
        updated[index][1] = training.name; // trainingName
        updated[index][5] = trainingCompMap[training.id]; // competencyId

        //Reset Expected Level
        updated[index][4] = ""; // levelId
        updated[index][6] = ""; // level name
        updated[index][7] = 0; // level value

        setSelectedLevelIds((prev) => ({
          ...prev,
          [index]: "", // Reset the level ID for this row
        }));
      }
    } else if (key === 2) {
      updated[index][2] = e.target.value; // Start date change
    } else if (key === 3) {
      updated[index][3] = e.target.value; // end date change
    } else if (key === 9) {
      const levelId = e.target.value;
      const competencyId = updated[index][5];
      const empJobId = empCompGaps[empId]?.[competencyId]?.jobId;
      const selectedLevel = jobLvls.find((lvl) => lvl.id === levelId);

      if (selectedLevel) {
        updated[index][6] = selectedLevel.name; // level name
        updated[index][7] = selectedLevel.value; // level value
        updated[index][4] = selectedLevel.id; // level ID

        setSelectedLevelIds((prev) => ({
          ...prev,
          [index]: selectedLevel.id,
        }));
      }
    } else if (key === 10) {
      const levelId = e.target.value;
      const competencyId = updated[index][5];
      const empJobId = empCompGaps[empId]?.[competencyId]?.jobId;
      const selectedLevel = jobLvls.find((lvl) => lvl.id === levelId);

      if (selectedLevel) {
        updated[index][10] = selectedLevel.name;
        updated[index][11] = selectedLevel.value;
        updated[index][9] = selectedLevel.id;

        // ✅ Detect change in achievedLevel and save immediately
        if (original && original[9] !== selectedLevel.id) {
          setSelectedEmpTraining(updated); // update first
          handleSaveTrainingRow(index); // then save
          return;
        }
      }
    }
    setSelectedEmpTraining(updated);
  };

  const toggleEditing = (index) => {
    setEditingRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));

    if (!editingRows[index]) {
      // entering edit mode → save original row for comparison
      setOriginalRows((prev) => ({
        ...prev,
        [index]: [...selectedEmpTraining[index]],
      }));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteTrainingRecordById(rowToDelete.id);
      if (res !== "error") {
        setSelectedEmpTraining((prev) => {
          const updated = [...prev];
          updated.splice(rowToDelete.index, 1);
          return updated;
        });
        snackbarRef.current?.showSnackbar(t("user.trainings.deletedSuccess"), "success");
      } else {
        // console.error("API returned error");
        snackbarRef.current?.showSnackbar(t("user.trainings.deletedFailed"), "error");
      }
    } catch (err) {
      // console.error("Delete error:", err);
      snackbarRef.current?.showSnackbar(t("user.trainings.deletedFailed"), "error");
    }

    setConfOpen(false); // always close dialog
  };

  //Saving Training Record (Draft)
  // Saving Training Record directly from child values
  const handleSaveDraftRow = async (empId, values) => {
    setSavingDraftEmpId(empId);

    const draft = values; // directly use the passed values

    // Validation
    if (!draft || !draft.trainingId || !draft.startDate || !draft.endDate || !draft.levelId) {
      snackbarRef.current?.showSnackbar(t("user.trainings.completeAllFields"), "error");
      setSavingDraftEmpId(null);
      return false;
    }

    const draftStart = new Date(draft.startDate);
    const draftEnd = new Date(draft.endDate);

    if (draftEnd < draftStart) {
      snackbarRef.current?.showSnackbar(t("user.trainings.endDateBeforeStart"), "error");
      setSavingDraftEmpId(null);
      return false;
    }

    const hasConflict = selectedEmpTraining.some((row) => {
      const existingStart = new Date(row[2]);
      const existingEnd = new Date(row[3]);
      return draftStart <= existingEnd && draftEnd >= existingStart;
    });

    if (hasConflict && !confirmedConflictEmpIds.has(empId)) {
      snackbarRef.current?.showSnackbar(t("user.trainings.trainingOverlap"), "warning");
      setConfirmedConflictEmpIds((prev) => new Set(prev).add(empId));
      setSavingDraftEmpId(null);
      return false;
    }

    const payload = {
      trainingId: draft.trainingId,
      employeeUuid: empId,
      startDate: formatToLocalDateTime(draft.startDate),
      endDate: formatToLocalDateTime(draft.endDate),
      expectedOutcomeLevel: draft.levelId,
      achievedLevel: null,
    };

    try {
      const result = await addTrainingRecord(payload);
      if (result !== "error") {
        snackbarRef.current?.showSnackbar(t("user.trainings.trainingAdded"), "success");

        const trainingName = (mapTraining[draft.competencyId] || []).find(
          (t) => t.id === draft.trainingId
        )?.name;

        const selectedLevel = jobLvls.find((lvl) => String(lvl.id) === String(draft.levelId));
        const levelName = selectedLevel?.name || "";
        const levelValue = selectedLevel?.value || 0;

        const newRow = [
          draft.trainingId, // trainingId
          trainingName, // trainingName
          draft.startDate, // startDate
          draft.endDate, // endDate
          draft.levelId,
          draft.competencyId,
          levelName, // expected outcome name
          levelValue, // expected outcome value
          result.id, // backend training ID
          draft.levelId, // expected outcome level ID
          null, // achieved level name
          null, // achieved level value
        ];

        setSelectedEmpTraining((prev) => [...prev, newRow]);

        setConfirmedConflictEmpIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(empId);
          return newSet;
        });
        return true;
      } else {
        snackbarRef.current?.showSnackbar(t("user.trainings.trainingAddedFailed"), "error");
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("user.trainings.trainingError"), "error");
      return false;
    }

    setSavingDraftEmpId(null);
  };

  const handleSaveTrainingRow = async (index) => {
    setSavingRowIndex(index);
    const currentRow = selectedEmpTraining[index];
    const originalRow = originalRows[index];

    if (!originalRow) {
      console.error("❌ No original data to compare.");
      return;
    }

    const recordId = currentRow[8];
    const competencyId = currentRow[5];
    const updatePayload = {};

    if (currentRow[0] !== originalRow[0]) {
      updatePayload.trainingId = currentRow[0]; // ✅ send trainingId
    }

    // Compare startDate
    if (currentRow[2] !== originalRow[2]) {
      updatePayload.startDate = new Date(currentRow[2]).toISOString();
    }

    // Compare endDate
    if (currentRow[3] !== originalRow[3]) {
      updatePayload.endDate = new Date(currentRow[3]).toISOString();
    }

    if (currentRow[4] !== originalRow[4]) {
      updatePayload.expectedOutcomeLevel = currentRow[4];
    }

    if (currentRow[9] !== originalRow[9]) {
      updatePayload.achievedLevel = currentRow[9];
    }

    if (Object.keys(updatePayload).length === 0) {
      toggleEditing(index);
      setSavingRowIndex(null);
      return;
    }

    const res = await updateTrainingRecord(recordId, updatePayload);

    if (res !== "error") {
      toggleEditing(index);
      snackbarRef.current?.showSnackbar(t("user.trainings.trainingUpdated"), "success");
    } else {
      console.error("❌ Failed to save row:", index);
      snackbarRef.current?.showSnackbar(t("user.trainings.trainingUpdatedFailed"), "error");
    }
    setSavingRowIndex(null);
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto", mt: 1, mb: 3 }}>
      <Button
        startIcon={<Icon>arrow_back</Icon>}
        sx={{
          color: primaryColor,
          borderColor: primaryColor,
          mb: 1,
          "&:hover": { backgroundColor: primaryColor, color: "#fff" },
        }}
        onClick={handleBack}
      >
        {t("user.myTeam.back")}
      </Button>

      <Paper
        sx={{
          p: 2,
          borderRadius: "12px",
          background: "#ffffff",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
          border: "1px solid #f1f5f9",
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            height: "100%",
          }}
        >
          {/* LEFT HALF — GAP ANALYSIS */}
          <GapAnalysisChart labels={labels} gapData={gapData} employeeName={employeeName} t={t} />

          {/* DIVIDER */}
          <Box
            sx={{
              width: { xs: "100%", md: "1px" },
              background: "#d1d5db", // darker divider
              height: { xs: 1, md: "auto" },
            }}
          />

          {/* RIGHT HALF — L&D PROGRESS */}
          <ProgressChart
            finishedTrainings={finishedTrainings}
            totalTrainings={totalTrainings}
            t={t}
          />
        </Box>
      </Paper>

      <Divider sx={{ my: 1, ml: 1, height: 2, backgroundColor: "#9ca3af" }} />

      <Typography variant="body2" fontWeight="bold" sx={{ ml: 1, color: "#1f2937" }}>
        {t("user.trainings.trainingsTableTitle") + " " + employeeName}
      </Typography>

      <Divider sx={{ my: 1, ml: 1, height: 2, backgroundColor: "#9ca3af" }} />

      {/* Training Card */}
      <Box display="flex" flexDirection="column" gap={3}>
        {selectedEmpTraining.map((row, index) => (
          <TrainingCard
            key={index}
            row={row}
            index={index}
            t={t}
            empId={empId}
            empTrainingOptions={empTrainingOptions}
            trainingCompMap={trainingCompMap}
            jobLvls={jobLvls}
            empCompGaps={empCompGaps}
            isEditing={editingRows?.[index]}
            savingRowIndex={savingRowIndex}
            selectedLevelIds={selectedLevelIds}
            toggleEditing={toggleEditing}
            handleSaveTrainingRow={handleSaveTrainingRow}
            handleTrainingFieldChange={handleTrainingFieldChange}
            handleAchievedLevelChange={handleAchievedLevelChange}
            setRowToDelete={setRowToDelete}
            setConfOpen={setConfOpen}
          />
        ))}
      </Box>

      <MDBox sx={{ position: "fixed", bottom: 20, right: 100 }}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => {
            setOpenTrainingDialog(true);
          }}
        >
          <Icon>add</Icon>
        </Fab>
      </MDBox>
      {openTrainingDialog && (
        <AddLAndDevDialog
          open={openTrainingDialog}
          onClose={() => setOpenTrainingDialog(false)}
          onSave={handleSaveDraftRow} // your save function
          empId={empId} // the current employee on this page
          empCompGaps={empCompGaps}
          empTrainingOptions={empTrainingOptions}
          trainingCompMap={trainingCompMap}
          jobLvls={jobLvls}
        />
      )}
      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </Box>
  );
};

export default LearningAndDevelopment;
